/** @format */

import {MouseEvent} from 'react';

import * as R from 'ramda';

import {useMediaQuery} from '@mui/material';

import {useTheme} from '@mui/material/styles';

import {Trans, useTranslation} from 'react-i18next';

import {platformsFromAdType, filterPlatformsByTokens} from 'helpers/adTypeHelpers';
import {reportLink} from 'helpers/reportHelpers';

import {useSearchParams} from 'helpers/routerHelpers';

import {useCompanyContext} from 'contexts/CompanyContext';

import {useMixpanelContext} from 'contexts/MixpanelContext';

import {LastCheckType, ReportFileFormat} from '../../types';
import {getIcon} from '../../helpers/getIcon';
import {Root, StyledLink, StyledText} from './styles';
import {GetCurrentUserQueryPage} from '../../graphql/__generated__';

interface Props {
  fileFormat: ReportFileFormat;
  share: boolean;
  data: GetCurrentUserQueryPage;
}

const DownloadLink = ({fileFormat, share, data}: Props) => {
  const searchParams = useSearchParams();
  const sensors = searchParams.get('sensors');
  const {currentAdType} = useCompanyContext();
  const {mixpanel} = useMixpanelContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {t} = useTranslation();

  if (!data?.currentUser?.currentCompany) {
    return null;
  }

  const {sensorExclusions, lastChecks, uuid, lastCheckCollectDate, tokens} =
    data.currentUser.currentCompany;

  const allSensors = R.pipe(
    R.map((v: LastCheckType) => v.sensor.name),
    R.without(sensorExclusions.map(v => v.sensorName)),
  )(lastChecks);

  const exportSensors = sensors || allSensors.join(',');

  const platforms = platformsFromAdType(currentAdType);

  const filteredPlatforms = filterPlatformsByTokens(tokens, platforms).join(',');

  const url = reportLink(
    data.currentUser.uuid,
    uuid,
    lastCheckCollectDate ?? '',
    exportSensors,
    filteredPlatforms,
    fileFormat,
    data.currentUser.lang ?? 'RU',
  );

  const handleClick = async (event: MouseEvent<HTMLAnchorElement>) => {
    mixpanel.track('click_report_download_link', {format: fileFormat});

    if (!isMobile) return;
    if (!share) return;
    if (!navigator.share) return;

    event.preventDefault();

    await navigator.share({
      title: 'AdSensor',
      text: t('Отчёт AdSensor', 'Отчёт AdSensor') || '',
      url: event.currentTarget.href,
    });
  };

  return (
    <Root>
      <StyledLink
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        onClick={handleClick}
        data-testid={`report-button-${fileFormat}`}
      >
        <img alt={fileFormat} src={getIcon(fileFormat)} />
        <StyledText>
          <Trans>Скачать</Trans> {fileFormat.toUpperCase()}
        </StyledText>
      </StyledLink>
    </Root>
  );
};

export {DownloadLink};
